import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import { shuffleArray } from 'shared';
import { StrapiBulkFurniturePartnersResponse } from 'types';
import hero from '../assets/furniture-partners/hero.jpeg';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { BlueArch, WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { DynamicLink } from './agencies';

import furnitureVolume from '../assets/furniture-partners/furniture-volume.png';

export async function getStaticProps() {
  const params = {
    populate: { logo: { fields: ['name', 'url', 'height', 'width'] } },
    pagination: { page: 1, pageSize: 100 },
  };

  const [data] = await strapi.get<StrapiBulkFurniturePartnersResponse>(`/api/furniture-partners`, { params });
  const partners = shuffleArray(data.data.map(p => p.attributes));

  return {
    props: { partners },
    revalidate: 21600,
  };
}

export default function BulkFurniturePartners({ partners }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main>
      <Meta title="bulk furniture partners" description="Beds for Kids Bulk Furniture Partners" noDefaultFooter />
      <section className="relative h-[70vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Bulk Furniture Partners"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="mx-auto max-w-3xl bg-white pb-12 text-blue-500">
        <h1 className="my-4 text-center font-operetta text-6xl">bulk furniture partners</h1>
        <p className="mx-auto px-4 text-center font-avenir text-xl leading-6">
          We rely on the generosity and involvement of bulk furniture partners. Do you have excess inventory, scratch &
          dent furniture, or returns? We would love to give these items a home with families in need.
        </p>
        <div className="mx-auto max-w-3xl px-8 pt-8 md:mx-8">
          <h4 className="text-center text-xl font-semibold">How this benefits your organization:</h4>
          <ul className="mt-4 list-decimal">
            <li className="list-item">
              Less unwanted products going to the landfill and therefore increasing sustainability of your organization.
            </li>
            <li className="list-item">You&#39;re able to write it off!</li>
            <li className="list-item">
              Donating to Beds for Kids could help community service initiatives and is a great philanthropic story.
            </li>
            <li className="list-item">
              You would be helping thousands of families work towards housing and financial stability by providing
              quality bedding or furniture.
            </li>
            <li className="list-item">
              If you are a local institution, we are also able to come pick up the furniture ourselves. We&#39;ve gone
              room by room through hotels and colleges in the past.
            </li>
          </ul>
        </div>
        <div className="mx-auto flex max-w-lg flex-col gap-4 py-8 text-center">
          <p className="w-full text-center">
            Contact our team to schedule your bulk furniture donation:{' '}
            <A className="hover:cursor-pointer hover:underline" external href="mailto:jporpilia@bedsforkids.org">
              jporpilia@bedsforkids.org
            </A>
          </p>
          <div>
            <Button link="mailto:jporpilia@bedsforkids.org">contact our team</Button>
          </div>
        </div>
      </section>
      <section className="bg-blue-500 px-8 py-16 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-5">
          <div className="flex w-full items-center justify-center text-center md:col-span-2">
            <h2 className="font-operetta text-5xl">items we accept</h2>
          </div>
          <div className="flex h-full flex-col justify-center text-center md:col-span-3 md:pb-0 md:pt-0 md:text-left">
            <p className="font-avenir text-lg leading-6">
              Items we accept include: bedding, lamps, bed frames and mattresses (twin, full, queen), sofas, kitchen
              tables, chairs, coffee tables, end tables, night stands, dressers, desks, bookshelves
            </p>
          </div>
          <div className="mt-4 flex items-center justify-center md:col-span-5">
            <Image
              src={furnitureVolume}
              alt="diagram showing how much furniture Beds for Kids needs each year to operate"
              className="rounded-lg"
            />
          </div>
        </div>
      </section>
      <section className="bg-white pt-12 text-blue-500">
        <h2 className="mx-auto mb-4 max-w-3xl text-center font-operetta text-6xl">
          current bulk
          <br />
          furniture partners
        </h2>
        <div className="mx-auto grid max-w-5xl grid-cols-2 items-center justify-center gap-x-12 border-b-2 border-green-500 px-4 pb-8 md:grid-cols-4">
          {partners.map(p => (
            <div className="flex aspect-1 items-center justify-center" key={`${p.name}`}>
              <DynamicLink href={p.link}>
                <Image
                  src={p.logo.data.attributes.url}
                  alt={p.name}
                  sizes="33vw, (min-width: 768px) 17vw"
                  height={p.logo.data.attributes.height}
                  width={p.logo.data.attributes.width}
                />
              </DynamicLink>
            </div>
          ))}
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-12 pt-8">
          <p className="text-center font-avenir text-xl leading-6">
            These furniture partners support us through bulk furniture donations.
          </p>
        </div>
        <BlueArch />
      </section>
      <section className="bg-blue-500 pb-12 pt-4 text-center text-white">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="font-operetta text-5xl">become a bulk furniture partner</h2>
          <h6 className="mt-8 font-avenir text-2xl font-medium">Think you might be a good fit for Beds for Kids?</h6>
          <p className="mt-8 font-avenir text-lg leading-6">
            Tell us a little bit about your organization! We are excited to hear from you!
          </p>
          <div className="mt-8 text-center">
            <Button link="mailto:jporpilia@bedsforkids.org" bType="secondary-white">
              contact us now!
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
